import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ListChecks, Send, RefreshCw, CheckCircle2, RotateCcw } from "lucide-react";
import { confirmDialog } from "@/lib/dialogs";

const STATUS = {
  not_started: { label: "Not Started", cls: "bg-slate-100 text-slate-600" },
  in_progress: { label: "In Progress", cls: "bg-amber-100 text-amber-700" },
  complete: { label: "Engager Setup Complete", cls: "bg-emerald-100 text-emerald-700" },
};

export default function EngagerSetupCard({ proposalId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const load = () => api.get(`/proposals/${proposalId}/engager`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [proposalId]);
  if (!data || !data.setup) return null;
  const { setup, progress, can_edit } = data;

  const assign = async (t, patch) => {
    try {
      const r = await api.post(`/engager-setups/${setup.id}/assign`, { task_id: t.task_id, assigned_user_id: t.assigned_user_id, assigned_user_name: t.assigned_user_name, due_date: t.due_date, ...patch });
      setData({ ...data, setup: r.data.setup, progress: r.data.progress });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); }
  };

  const toggle = async (taskId, completed) => {
    if (!can_edit) return toast.error("You are not authorised to update Engager setup");
    setBusy(true);
    try {
      const r = await api.post(`/engager-setups/${setup.id}/toggle`, { task_id: taskId, completed });
      setData({ ...data, setup: r.data.setup, progress: r.data.progress });
      if (r.data.progress.complete) toast.success("Engager setup complete — Proposal Owner notified");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update task"); }
    setBusy(false);
  };
  const resend = async (which) => {
    try {
      const r = await api.post(`/engager-setups/${setup.id}/resend-${which}`);
      toast[r.data.sent ? "success" : "error"](r.data.sent ? "Email resent" : "Could not send email");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
  };
  const forceComplete = async () => {
    if (!(await confirmDialog({ title: "Mark Engager setup complete?", description: "Use this when the client is already onboarded (e.g. an existing client) and these tasks don't need doing. It moves to Complete and notifies the Proposal Owner.", confirmText: "Mark complete" }))) return;
    setBusy(true);
    try {
      const r = await api.post(`/engager-setups/${setup.id}/complete`, { note: "Existing client — already onboarded" });
      setData({ ...data, setup: r.data.setup, progress: r.data.progress });
      toast.success("Engager setup marked complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not complete"); }
    setBusy(false);
  };
  const reopen = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/engager-setups/${setup.id}/reopen`);
      setData({ ...data, setup: r.data.setup, progress: r.data.progress });
      toast.success("Engager setup reopened");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reopen"); }
    setBusy(false);
  };
  const st = STATUS[setup.status] || STATUS.not_started;
  const perMap = Object.fromEntries((progress.per_service || []).map((s) => [s.service_id, s]));

  return (
    <Card className="p-6 rounded-md" data-testid="engager-setup-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-alt font-semibold text-cass-navy flex items-center gap-2"><ListChecks className="w-5 h-5 text-cass-teal" />Engager Setup</h3>
        <Badge className={st.cls} data-testid="engager-overall-status">{st.label}</Badge>
      </div>
      <p className="text-sm text-cass-slate mb-3" data-testid="engager-overall-progress">{progress.completed} of {progress.total} tasks completed — {progress.percent}%</p>
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
        <div className="h-full bg-cass-teal transition-all" style={{ width: `${progress.percent}%` }} />
      </div>
      {setup.force_complete && (
        <p className="text-xs text-emerald-700 -mt-2 mb-4 flex items-center gap-1" data-testid="engager-forced-note">
          <CheckCircle2 className="w-3.5 h-3.5" />Marked complete — existing client already onboarded.
        </p>
      )}

      <div className="space-y-4">
        {setup.services.map((g) => {
          const ps = perMap[g.service_id] || { completed: 0, total: g.tasks.length, status: "not_started" };
          return (
            <div key={g.service_id} data-testid={`engager-service-${g.service_id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-cass-navy">{g.name}</div>
                <span className="text-xs text-cass-slate">{ps.total ? `${ps.completed}/${ps.total} ${ps.status === "complete" ? "Complete" : ps.status === "in_progress" ? "In Progress" : ""}` : "No tasks configured"}</span>
              </div>
              <div className="mt-2 space-y-1.5">
                {g.tasks.map((t) => {
                  const overdue = t.due_date && !t.completed && new Date(t.due_date) < new Date(new Date().toDateString());
                  return (
                  <div key={t.task_id} data-testid={`engager-task-${t.task_id}`}>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <Checkbox checked={t.completed} disabled={busy || !can_edit} onCheckedChange={(v) => toggle(t.task_id, !!v)} data-testid={`engager-check-${t.task_id}`} className="mt-0.5" />
                      <span className={t.completed ? "line-through text-cass-slate" : "text-cass-navy"}>
                        {t.title}
                        {t.completed && t.completed_by && <span className="text-[11px] text-cass-slate ml-2">✓ {t.completed_by}{t.completed_at ? ` · ${new Date(t.completed_at).toLocaleDateString("en-GB")}` : ""}</span>}
                      </span>
                    </label>
                    {can_edit && !t.completed && (
                      <div className="flex items-center gap-2 ml-6 mt-1">
                        <Select value={t.assigned_user_id || "__none"} onValueChange={(v) => assign(t, { assigned_user_id: v === "__none" ? null : v, assigned_user_name: v === "__none" ? null : (users.find((u) => u.id === v)?.name) })}>
                          <SelectTrigger className="h-7 w-44 text-xs" data-testid={`assign-${t.task_id}`}><SelectValue placeholder="Assign to…" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="date" className="h-7 w-36 text-xs" value={t.due_date || ""} onChange={(e) => assign(t, { due_date: e.target.value })} data-testid={`due-${t.task_id}`} />
                      </div>
                    )}
                    {(t.assigned_user_name || t.due_date) && (
                      <div className={`text-[11px] ml-6 ${overdue ? "text-red-600 font-medium" : "text-cass-slate"}`}>
                        {t.assigned_user_name ? `Assigned: ${t.assigned_user_name}` : "Unassigned"}{t.due_date ? ` · due ${new Date(t.due_date).toLocaleDateString("en-GB")}${overdue ? " (overdue)" : ""}` : ""}
                      </div>
                    )}
                  </div>
                );})}
                {!g.tasks.length && <div className="text-xs text-cass-slate italic">No Engager setup tasks currently configured.</div>}
              </div>
            </div>
          );
        })}
      </div>

      {can_edit && (
        <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-border">
          <Button size="sm" variant="outline" onClick={() => resend("notification")} data-testid="engager-resend-notif"><Send className="w-3.5 h-3.5 mr-1" />Resend setup email</Button>
          {!progress.complete && <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={busy} onClick={forceComplete} data-testid="engager-force-complete"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Mark complete (existing client)</Button>}
          {setup.force_complete && <Button size="sm" variant="outline" disabled={busy} onClick={reopen} data-testid="engager-reopen"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reopen setup</Button>}
          {progress.complete && <Button size="sm" variant="outline" onClick={() => resend("completion")} data-testid="engager-resend-complete"><RefreshCw className="w-3.5 h-3.5 mr-1" />Resend completion email</Button>}
        </div>
      )}
    </Card>
  );
}
